const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

function getBrowserCookie(name) {
  if (!isBrowser()) {
    return null;
  }

  let cookieArray = document.cookie.split(";");
  for (let i = 0; i < cookieArray.length; i++) {
    let cookiePair = cookieArray[i].trim();
    if (cookiePair.startsWith(name + "=")) {
      // İlk = karakterinden sonrasını al (cookie değeri içinde = olabilir)
      let cookieValue = cookiePair.substring(name.length + 1);
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
}

function isRefreshTokenExist() {
  const refreshTokenCookie = getBrowserCookie("u7Bn3kD8sL4yXzF");
  if (refreshTokenCookie) {
    return refreshTokenCookie !== null;
  }
  const userInfoCookie = getBrowserCookie("userInfo");
  if (userInfoCookie) {
    try {
      const userInfoParsed = JSON.parse(userInfoCookie);
      return userInfoParsed && userInfoParsed.rm;
    } catch (error) {
      console.error("UserInfo parse error:", error);
      return false;
    }
  }
  return false;
}

function bindUtagDataForMobileApp(jwt, refreshToken, userInfo) {
  const userInfoParsed = userInfo ? JSON.parse(userInfo) : {};
  const userObject = userInfoParsed
    ? {
        email: userInfoParsed.e,
        name: userInfoParsed.fn,
        userId: userInfoParsed.uid,
        hasApprovedPolicies: "",
        gender: "",
      }
    : {
        email: null,
        name: null,
        userId: null,
        hasApprovedPolicies: null,
        gender: null,
      };

  const appData = {
    jwtToken: jwt,
    refreshToken: refreshToken,
    token: null,
    user: userObject,
    userInfo: userInfo,
    type: null,
    returnUrl: null,
    fingerprint: null,
  };

  const JSAndroidUtagData = () => {
    // eslint-disable-next-line no-undef
    if (
      typeof Android !== "undefined" &&
      typeof Android.utagDataReceived !== "undefined"
    ) {
      // eslint-disable-next-line no-undef
      Android.utagDataReceived(JSON.stringify(appData));
    }

    if (
      typeof window.webkit !== "undefined" &&
      typeof window.webkit.messageHandlers !== "undefined"
    ) {
      window.webkit.messageHandlers.UtagData.postMessage(appData);
    }
  };

  if (isBrowser()) {
    JSAndroidUtagData();
  }
}

function base64urlToBase64(base64url) {
  let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");

  const padding = base64.length % 4;
  if (padding) {
    base64 += "=".repeat(4 - padding);
  }

  return base64;
}

function safeBase64urlDecode(base64urlString) {
  try {
    const base64String = base64urlToBase64(base64urlString);
    return atob(base64String);
  } catch (error) {
    console.error("Base64url decode error:", error);
    throw new Error("Invalid base64url string");
  }
}

function safeJwtDecode(jwt) {
  try {
    if (!jwt || typeof jwt !== "string") {
      throw new Error("Invalid JWT: token is empty or not a string");
    }

    const parts = jwt.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT: token must have 3 parts");
    }

    const payload = parts[1];
    const decodedPayload = safeBase64urlDecode(payload);

    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error("JWT decode error:", error);
    throw new Error(`JWT decode failed: ${error.message}`);
  }
}

function isTokenExpired() {
  const jwt = getBrowserCookie("jwt");
  if (jwt) {
    try {
      const decodedJwt = safeJwtDecode(jwt);
      return Date.now() >= decodedJwt.exp * 1000;
    } catch (error) {
      console.error("JWT token decode error:", error);
      return true; // Hata durumunda expired kabul et
    }
  } else {
    const userInfo = getBrowserCookie("userInfo");
    if (userInfo) {
      try {
        const userInfoParsed = JSON.parse(userInfo);
        return Date.now() >= userInfoParsed.exp * 1000;
      } catch (error) {
        console.error("UserInfo parse error:", error);
        return true; // Hata durumunda expired kabul et
      }
    }
    return false;
  }
}

function xsrfToken(headers, baseUrl = "https://oauth.test.hepsiburada.com") {
  return fetch(`${baseUrl}/api/authenticate/xsrf-token`, {
    method: "GET",
    credentials: "include",
    headers: {
      ...headers,
    },
  });
}

async function createHeaders(
  defaultHeaders = {},
  baseUrl = "https://oauth.test.hepsiburada.com"
) {
  const REFRESH_COOKIE_NAME = "u7Bn3kD8sL4yXzF";
  const XSRF_HEADER_NAME = "X-XSRF-TOKEN";
  const X_DEVICE_TYPE_HEADER_NAME = "X-Device-Type";
  const headers = { ...defaultHeaders };

  const refreshCookie = getBrowserCookie(REFRESH_COOKIE_NAME);
  const deviceType = getBrowserCookie(X_DEVICE_TYPE_HEADER_NAME);
  if (deviceType) {
    headers[X_DEVICE_TYPE_HEADER_NAME] = deviceType;
  }

  try {
    const xsrfTokenResponse = await xsrfToken(headers, baseUrl);
    const xsrfTokenData = xsrfTokenResponse
      ? await xsrfTokenResponse.json()
      : null;
    if (xsrfTokenData && xsrfTokenData.xsrfToken) {
      headers[XSRF_HEADER_NAME] = xsrfTokenData.xsrfToken;
    }
  } catch (error) {
    console.error("Error while getting xsrf token:", error);
  }

  if (refreshCookie) {
    headers[REFRESH_COOKIE_NAME] = refreshCookie;
  }

  return headers;
}

function refreshToken(headers, baseUrl = "https://oauth.test.hepsiburada.com") {
  return fetch(`${baseUrl}/api/authenticate/refresh-token`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...headers,
    },
  });
}

// Lock mekanizması için global değişkenler
let isRefreshing = false;
let failedQueue = [];
let interceptorAddedInstances = new Set();

// Bekleyen istekleri işlemek için fonksiyon
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Axios instance'a token yenileme interceptor'ı ekler
 * @param {Object} axiosInstance - Axios instance
 * @param {Object} options - Konfigürasyon seçenekleri
 * @param {string} options.environment - Ortam belirteci ('test' veya 'prod') (default: 'test')
 * @param {string} options.baseUrl - OAuth base URL (opsiyonel, environment'a göre otomatik belirlenir)
 * @param {string} options.refreshCookieName - Refresh token cookie adı (default: u7Bn3kD8sL4yXzF)
 * @param {string} options.jwtCookieName - JWT cookie adı (default: jwt)
 * @param {string} options.userInfoCookieName - UserInfo cookie adı (default: userInfo)
 */
function addCustomInterceptor(axiosInstance, options = {}) {
  // Eğer bu instance için interceptor zaten eklenmişse, tekrar ekleme
  if (interceptorAddedInstances.has(axiosInstance)) {
    console.warn(
      "Refresh token interceptor already added to this instance. Skipping..."
    );
    return;
  }

  // Environment'a göre base URL belirleme
  const getBaseUrlByEnvironment = (environment) => {
    switch (environment) {
      case "prod":
        return "https://oauth.hepsiburada.com";
      case "test":
      default:
        return "https://oauth.test.hepsiburada.com";
    }
  };

  const config = Object.assign(
    {},
    {
      environment: "test",
      refreshCookieName: "u7Bn3kD8sL4yXzF",
      jwtCookieName: "jwt",
      userInfoCookieName: "userInfo",
    },
    options || {}
  );

  config.baseUrl =
    (options && options.baseUrl) || getBaseUrlByEnvironment(config.environment);

  // Mevcut interceptor'ları koru ve yeni interceptor'ı ekle
  const existingResponseInterceptors = axiosInstance.interceptors.response;

  // Yeni interceptor'ı ekle
  existingResponseInterceptors.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        originalRequest &&
        originalRequest.data &&
        typeof originalRequest.data === "string" &&
        originalRequest.data.trim() !== ""
      ) {
        try {
          originalRequest.data = JSON.parse(originalRequest.data);
        } catch (parseError) {
          console.warn("Failed to parse originalRequest.data:", parseError);
          // JSON parse başarısız olursa data'yı olduğu gibi bırak
        }
      }
      if (
        error.response &&
        error.response.status === 401 &&
        isRefreshTokenExist() &&
        isTokenExpired()
      ) {
        // Eğer token refresh zaten devam ediyorsa, bu isteği kuyruğa ekle
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, originalRequest });
          })
            .then((token) => {
              // Token refresh tamamlandıktan sonra orijinal isteği yeniden gönder
              // Yeni JWT'yi al ve originalRequest header'larını güncelle
              const newJwt = getBrowserCookie(config.jwtCookieName);
              if (newJwt) {
                originalRequest.headers = originalRequest.headers || {};
                // Eğer cookie header'ı kullanılıyorsa onu da güncelle
                originalRequest.headers["Cookie"] = originalRequest.headers[
                  "Cookie"
                ]
                  ? originalRequest.headers["Cookie"].replace(
                      /jwt=[^;]*/g,
                      `jwt=${newJwt}`
                    )
                  : `jwt=${newJwt}`;
              }
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        // İlk refresh isteği - lock'u al
        isRefreshing = true;

        try {
          // Refresh endpoint için temiz headers oluştur (orijinal header'ları kullanma)
          const refreshHeaders = await createHeaders({}, config.baseUrl);
          const tokenResponse = await refreshToken(
            refreshHeaders,
            config.baseUrl
          );
          const tokenResponseData = tokenResponse
            ? await tokenResponse.json()
            : null;

          if (
            !tokenResponse ||
            tokenResponse.status !== 200 ||
            (tokenResponseData && !tokenResponseData.success)
          ) {
            // Refresh başarısız - kuyrukta bekleyen istekleri reddet
            processQueue(error, null);
            return Promise.reject(error);
          }

          // Refresh başarılı - kuyrukta bekleyen tüm istekleri başarıyla işle
          processQueue(null, tokenResponse);

          const userInfo = getBrowserCookie(config.userInfoCookieName);
          const tokenData =
            tokenResponseData && tokenResponseData.data
              ? tokenResponseData.data
              : {};
          bindUtagDataForMobileApp(
            tokenData.token,
            tokenData.refreshToken,
            userInfo
          );

          // Yeni JWT'yi al ve originalRequest header'larını güncelle
          const newJwt = getBrowserCookie(config.jwtCookieName);
          if (newJwt) {
            originalRequest.headers = originalRequest.headers || {};
            // Eğer cookie header'ı kullanılıyorsa onu da güncelle
            originalRequest.headers["Cookie"] = originalRequest.headers[
              "Cookie"
            ]
              ? originalRequest.headers["Cookie"].replace(
                  /jwt=[^;]*/g,
                  `jwt=${newJwt}`
                )
              : `jwt=${newJwt}`;
          }

          // Orijinal istek güncellenmiş header'larla tekrar gönderilir
          return axiosInstance(originalRequest);
        } catch (tokenError) {
          // Hata durumunda kuyrukta bekleyen istekleri reddet
          processQueue(tokenError, null);
          return Promise.reject(tokenError);
        } finally {
          // Her durumda lock'u serbest bırak
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );

  // Bu instance için interceptor eklendiğini işaretle
  interceptorAddedInstances.add(axiosInstance);
}

// CommonJS uyumluluğu için
if (typeof module !== "undefined" && module.exports) {
  module.exports = addCustomInterceptor;
  module.exports.addCustomInterceptor = addCustomInterceptor;
  module.exports.default = addCustomInterceptor;
}

export { addCustomInterceptor, addCustomInterceptor as default };
